// Story 3.7d — shared hook: polling via @tanstack/react-query (30s) +
// Supabase Realtime INSERT subscription on audit_log for instant flag bump.
//
// Why useQuery directly (not Refine `useCustom`): Story 3.7b explicitly does
// NOT implement `custom` on filter-api data provider. useQuery is the
// surgical alternative — see Story 3.7d Dev Notes §5.

import { useEffect, useId } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { filterApiFetch } from '../../lib/filter-api-client';
import { supabaseClient } from '../../supabase-client';

export type DashboardScope = 'community' | 'organization';

export interface DashboardMetricsData {
  scope: DashboardScope;
  scopeId: string;
  wau: number;
  activeConversations: number;
  flaggedIncidents: { last24h: number; last7d: number };
  // Literals match the backend contract (apps/filter-api/src/lib/dashboard-metrics.ts).
  // When Эпик 10 wires real cost, widen both sides simultaneously — TS errors
  // here surface every call-site that needs to update.
  inferenceCost: null;
  costMetricAvailable: false;
  asOf: string;
}

const POLL_INTERVAL_MS = 30_000;

function buildUrl(scope: DashboardScope, id: string): string {
  return scope === 'community'
    ? `/v1/communities/${id}/dashboard`
    : `/v1/organizations/${id}/dashboard`;
}

export function dashboardQueryKey(scope: DashboardScope, id: string): readonly unknown[] {
  return ['dashboard', scope, id];
}

export function useDashboardMetrics(scope: DashboardScope, id: string) {
  const queryClient = useQueryClient();
  // Per-mount nonce avoids channel-name collisions on fast re-mounts
  // (StrictMode dev double-invoke, rapid route changes) — two channels with
  // the same topic name would share a subscription and the first unmount
  // would kill it for the still-mounted one.
  const channelNonce = useId();

  const query = useQuery<DashboardMetricsData>({
    queryKey: dashboardQueryKey(scope, id),
    queryFn: async () => {
      // filterApiFetch unwraps the envelope's `data` field. The endpoint
      // returns `{ data: DashboardMetricsData, request_id }` so this gives us
      // the inner metrics object directly.
      const data = await filterApiFetch<DashboardMetricsData>(buildUrl(scope, id));
      return data;
    },
    refetchInterval: POLL_INTERVAL_MS,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (!id) return;
    const filter =
      scope === 'community'
        ? `community_id=eq.${id}`
        : `organization_id=eq.${id}`;

    // Belt-and-suspenders: ensure the realtime socket has the authenticated
    // JWT before subscribing. supabase-js v2 propagates auth-state changes
    // automatically, but mount-before-auth-settled or refresh-after-mount
    // can race. setAuth is idempotent and safe to call repeatedly.
    let cancelled = false;
    void supabaseClient.auth.getSession().then(({ data }) => {
      if (cancelled) return;
      const token = data.session?.access_token;
      if (token) supabaseClient.realtime.setAuth(token);
    });

    const channel = supabaseClient
      .channel(`audit-flagged-${scope}-${id}-${channelNonce}`)
      .on(
        'postgres_changes' as never,
        {
          event: 'INSERT',
          schema: 'public',
          table: 'audit_log',
          filter,
        },
        (payload: { new?: { filter_decision?: string } }) => {
          const decision = payload.new?.filter_decision;
          if (decision !== 'block_pre_filter' && decision !== 'block_post_filter') return;
          // Optimistic local bump; next 30s poll provides authoritative value.
          queryClient.setQueryData<DashboardMetricsData>(
            dashboardQueryKey(scope, id),
            (prev) => {
              if (!prev) return prev;
              return {
                ...prev,
                flaggedIncidents: {
                  last24h: prev.flaggedIncidents.last24h + 1,
                  last7d: prev.flaggedIncidents.last7d + 1,
                },
              };
            },
          );
        },
      )
      .subscribe();

    return () => {
      cancelled = true;
      void channel.unsubscribe();
    };
  }, [scope, id, queryClient, channelNonce]);

  return query;
}
